/**
 * Employee KPI self-service page.
 * - Employees add their own individual KPIs for the year (targets differ per quarter)
 * - HR provides corporate KPIs (read-only for employees)
 * - Employee submits plan → manager approves / rejects
 * - Approved plans are locked
 */

import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import { useAuth } from '@/core/auth/AuthContext'
import type {
  AssessmentPeriod,
  KPIAssignment,
  KPIPlan,
  CreateMyKPIInput,
  KPIPlanStatus,
} from '../types/penilaian.types'
import {
  KPI_PLAN_STATUS_LABELS,
  KPI_PLAN_STATUS_COLORS,
} from '../types/penilaian.types'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AnnualKPIRow {
  name: string
  description?: string | null
  annual_target?: string | null
  kpi_type: 'company' | 'individual'
  department?: string | null
  q1?: KPIAssignment
  q2?: KPIAssignment
  q3?: KPIAssignment
  q4?: KPIAssignment
}

type YearPeriods = {
  Q1?: AssessmentPeriod
  Q2?: AssessmentPeriod
  Q3?: AssessmentPeriod
  Q4?: AssessmentPeriod
}

const QUARTER_FIELDS = [
  { q: 'Q1' as const, key: 'q1' as const, field: 'q1_target' as const },
  { q: 'Q2' as const, key: 'q2' as const, field: 'q2_target' as const },
  { q: 'Q3' as const, key: 'q3' as const, field: 'q3_target' as const },
  { q: 'Q4' as const, key: 'q4' as const, field: 'q4_target' as const },
]

// ─── Plan status badge ────────────────────────────────────────────────────────

function PlanStatusBadge({ status }: { status: KPIPlanStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${KPI_PLAN_STATUS_COLORS[status]}`}
    >
      {KPI_PLAN_STATUS_LABELS[status]}
    </span>
  )
}

// ─── KPI annual table (read-only variant for individual too) ──────────────────

interface KPIAnnualTableProps {
  rows: AnnualKPIRow[]
  yearPeriods: YearPeriods
  canEdit?: boolean
  onEdit?: (row: AnnualKPIRow) => void
  onDelete?: (row: AnnualKPIRow) => void
  onEditRealisasi?: (asgn: KPIAssignment, q: 'Q1' | 'Q2' | 'Q3' | 'Q4') => void
}

function KPIAnnualTable({
  rows,
  yearPeriods,
  canEdit = false,
  onEdit,
  onDelete,
  onEditRealisasi,
}: KPIAnnualTableProps) {
  if (rows.length === 0) return null

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="text-left px-4 py-2.5 font-medium text-gray-600">
              Nama KPI
            </th>
            <th className="text-center px-3 py-2.5 font-medium text-gray-600 w-16">
              Bobot
            </th>
            <th className="text-left px-3 py-2.5 font-medium text-gray-600 w-36">
              Target Tahunan
            </th>
            {QUARTER_FIELDS.map(({ q }) => (
              <th
                key={q}
                className={`text-left px-3 py-2.5 font-medium w-32 ${
                  yearPeriods[q] ? 'text-gray-600' : 'text-gray-300'
                }`}
              >
                {q}
                {yearPeriods[q] && (
                  <span className="ml-1 text-xs font-normal text-gray-400">
                    ({yearPeriods[q]!.status})
                  </span>
                )}
              </th>
            ))}
            {canEdit && <th className="w-20" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((row, i) => {
            const bobot =
              row.q1?.bobot ?? row.q2?.bobot ?? row.q3?.bobot ?? row.q4?.bobot ?? 0
            return (
              <tr
                key={`${row.name}-${i}`}
                className="hover:bg-gray-50/60 group"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{row.name}</div>
                  {row.description && (
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {row.description}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-center font-semibold text-gray-800">
                  {bobot}%
                </td>
                <td className="px-3 py-3">
                  {row.annual_target ? (
                    <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                      {row.annual_target}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300 italic">—</span>
                  )}
                </td>
                {QUARTER_FIELDS.map(({ q, key }) => {
                  const period = yearPeriods[q]
                  const asgn = row[key]
                  if (!period) {
                    return (
                      <td key={q} className="px-3 py-3">
                        <span className="text-gray-200 text-xs">—</span>
                      </td>
                    )
                  }
                  if (!asgn) {
                    return (
                      <td key={q} className="px-3 py-3">
                        <span className="text-xs text-gray-300 italic">—</span>
                      </td>
                    )
                  }
                  const target = asgn.target_value
                  const isOpen = period.status === 'open'
                  return (
                    <td key={q} className="px-3 py-3">
                      <div
                        className="text-xs text-gray-700 line-clamp-2"
                        title={target ?? ''}
                      >
                        {target ?? (
                          <span className="text-gray-300 italic">No target</span>
                        )}
                      </div>
                      {isOpen && onEditRealisasi ? (
                        <button
                          onClick={() => onEditRealisasi(asgn, q)}
                          className="mt-1 flex items-center gap-1 text-left w-full group/real"
                        >
                          {asgn.realisasi ? (
                            <>
                              <span className="text-xs text-gray-500 truncate max-w-[60px]">
                                {asgn.realisasi}
                              </span>
                              {asgn.realisasi_pct != null && (
                                <span className={`text-xs font-semibold ${
                                  asgn.realisasi_pct >= 100 ? 'text-green-600'
                                  : asgn.realisasi_pct >= 75 ? 'text-blue-500'
                                  : 'text-red-400'
                                }`}>
                                  {asgn.realisasi_pct}%
                                </span>
                              )}
                              <svg className="w-3 h-3 text-gray-300 group-hover/real:text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </>
                          ) : (
                            <span className="text-xs text-blue-500 underline underline-offset-2">
                              + Isi realisasi
                            </span>
                          )}
                        </button>
                      ) : (
                        <div className="mt-1 flex items-center gap-1">
                          {asgn.realisasi && (
                            <span className="text-xs text-gray-400 truncate max-w-[70px]">
                              {asgn.realisasi}
                            </span>
                          )}
                          {asgn.realisasi_pct != null && (
                            <span className={`text-xs font-semibold ${
                              asgn.realisasi_pct >= 100 ? 'text-green-600'
                              : asgn.realisasi_pct >= 75 ? 'text-blue-500'
                              : 'text-red-400'
                            }`}>
                              {asgn.realisasi_pct}%
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  )
                })}
                {canEdit && (
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onEdit?.(row)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete?.(row)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Hapus"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Add/Edit individual KPI modal ────────────────────────────────────────────

interface EditKPIModalProps {
  row: AnnualKPIRow | null // null = add mode
  yearPeriods: YearPeriods
  employeeId: string
  year: number
  onClose: () => void
}

function EditKPIModal({
  row,
  yearPeriods,
  employeeId,
  year,
  onClose,
}: EditKPIModalProps) {
  const qc = useQueryClient()
  const isAdd = row === null
  const [form, setForm] = useState({
    name: row?.name ?? '',
    target_description: row?.description ?? '',
    annual_target: row?.annual_target ?? '',
    bobot: String(
      row?.q1?.bobot ?? row?.q2?.bobot ?? row?.q3?.bobot ?? row?.q4?.bobot ?? ''
    ),
    q1_target: row?.q1?.target_value ?? '',
    q2_target: row?.q2?.target_value ?? '',
    q3_target: row?.q3?.target_value ?? '',
    q4_target: row?.q4?.target_value ?? '',
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (isAdd) {
        const input: CreateMyKPIInput = {
          employee_id: employeeId,
          year,
          name: form.name,
          target_description: form.target_description || form.name,
          annual_target: form.annual_target || null,
          bobot: Number(form.bobot),
          q1_target: form.q1_target || null,
          q2_target: form.q2_target || null,
          q3_target: form.q3_target || null,
          q4_target: form.q4_target || null,
        }
        return penilaianApi.createEmployeeKPI(input)
      } else {
        // Update: patch each existing quarterly assignment + definition
        const quarters = [
          { key: 'q1' as const, field: 'q1_target' as const },
          { key: 'q2' as const, field: 'q2_target' as const },
          { key: 'q3' as const, field: 'q3_target' as const },
          { key: 'q4' as const, field: 'q4_target' as const },
        ]
        for (const { key, field } of quarters) {
          const asgn = row![key]
          if (!asgn) continue
          await penilaianApi.updateKPIDefinition(asgn.kpi_id, {
            name: form.name,
            target_description: form.target_description || form.name,
            annual_target: form.annual_target || null,
          })
          await penilaianApi.updateKPIAssignment(asgn.id, {
            bobot: Number(form.bobot),
            target_value: form[field] || null,
          })
        }
      }
    },
    onSuccess: () => {
      Object.values(yearPeriods)
        .filter(Boolean)
        .forEach((p) =>
          qc.invalidateQueries({
            queryKey: ['my-kpi-annual', p!.id, employeeId],
          })
        )
      toast.success(isAdd ? 'KPI berhasil ditambahkan' : 'KPI berhasil diperbarui')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menyimpan KPI'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="font-semibold text-gray-900 text-lg">
            {isAdd ? 'Tambah KPI Individu' : 'Edit KPI'}
          </h3>
          {isAdd && (
            <p className="text-xs text-gray-500 mt-0.5">
              KPI dibuat untuk semua kuartal yang tersedia
            </p>
          )}
        </div>

        <form
          id="my-kpi-form"
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate()
          }}
          className="overflow-y-auto flex-1 px-6 py-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama KPI <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi / Cara Ukur <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.target_description}
              onChange={(e) =>
                setForm({ ...form, target_description: e.target.value })
              }
              rows={2}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bobot (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={form.bobot}
              onChange={(e) => setForm({ ...form, bobot: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Tahunan
            </label>
            <input
              type="text"
              value={form.annual_target}
              onChange={(e) => setForm({ ...form, annual_target: e.target.value })}
              placeholder="Contoh: 95% customer satisfaction (opsional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target per Kuartal
            </label>
            <div className="space-y-2">
              {QUARTER_FIELDS.map(({ q, key, field }) => {
                const exists = !!yearPeriods[q]
                const hasAsgn = !isAdd ? !!row![key] : exists
                return (
                  <div
                    key={q}
                    className={`flex items-center gap-3 ${!exists ? 'opacity-40' : ''}`}
                  >
                    <span className="text-xs font-bold text-gray-500 w-7">{q}</span>
                    <input
                      type="text"
                      value={form[field]}
                      onChange={(e) =>
                        setForm({ ...form, [field]: e.target.value })
                      }
                      placeholder={
                        !exists
                          ? 'Periode belum dibuat'
                          : !hasAsgn
                          ? 'Tidak diassign'
                          : `Target ${q}…`
                      }
                      disabled={!exists || (!isAdd && !hasAsgn)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            type="submit"
            form="my-kpi-form"
            disabled={mutation.isPending}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menyimpan…' : isAdd ? 'Tambah KPI' : 'Simpan'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Realisasi modal ──────────────────────────────────────────────────────────

interface RealisasiModalProps {
  asgn: KPIAssignment
  quarterLabel: string
  periodId: string
  employeeId: string
  onClose: () => void
}

function RealisasiModal({
  asgn,
  quarterLabel,
  periodId,
  employeeId,
  onClose,
}: RealisasiModalProps) {
  const qc = useQueryClient()
  const [realisasi, setRealisasi] = useState(asgn.realisasi ?? '')
  const [realisasiPct, setRealisasiPct] = useState(
    asgn.realisasi_pct != null ? String(asgn.realisasi_pct) : ''
  )
  const [comment, setComment] = useState(asgn.realisasi_comment ?? '')

  const mutation = useMutation({
    mutationFn: () =>
      penilaianApi.updateRealisasi(asgn.id, {
        realisasi,
        realisasi_pct: realisasiPct !== '' ? Number(realisasiPct) : null,
        realisasi_comment: comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-kpi-annual', periodId, employeeId] })
      toast.success('Realisasi berhasil disimpan')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menyimpan realisasi'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Input Realisasi</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {asgn.kpi?.name} — {quarterLabel}
          </p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate()
          }}
          className="px-5 py-4 space-y-4"
        >
          <div>
            <div className="text-xs text-gray-500 mb-1">
              Target:{' '}
              <span className="font-medium text-gray-700">
                {asgn.target_value ?? '—'}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Realisasi <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={realisasi}
              onChange={(e) => setRealisasi(e.target.value)}
              required
              placeholder="Deskripsi capaian aktual…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pencapaian (%)
            </label>
            <input
              type="number"
              min={0}
              max={200}
              step={0.1}
              value={realisasiPct}
              onChange={(e) => setRealisasiPct(e.target.value)}
              placeholder="Contoh: 95"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Komentar / Catatan
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Penjelasan tambahan, hambatan, atau konteks capaian…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {mutation.isPending ? 'Menyimpan…' : 'Simpan'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Delete confirmation modal ────────────────────────────────────────────────

interface DeleteKPIModalProps {
  row: AnnualKPIRow
  yearPeriods: YearPeriods
  employeeId: string
  onClose: () => void
}

function DeleteKPIModal({
  row,
  yearPeriods,
  employeeId,
  onClose,
}: DeleteKPIModalProps) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: async () => {
      const assignments = [row.q1, row.q2, row.q3, row.q4].filter(
        Boolean
      ) as KPIAssignment[]
      for (const asgn of assignments) {
        await penilaianApi.deleteKPIAssignment(asgn.id)
      }
    },
    onSuccess: () => {
      Object.values(yearPeriods)
        .filter(Boolean)
        .forEach((p) =>
          qc.invalidateQueries({
            queryKey: ['my-kpi-annual', p!.id, employeeId],
          })
        )
      toast.success('KPI dihapus')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menghapus KPI'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-5 h-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Hapus KPI?</h3>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-medium text-gray-700">{row.name}</span> akan
              dihapus dari semua kuartal.
            </p>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menghapus…' : 'Ya, Hapus'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MyKPIPage() {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [addModal, setAddModal] = useState(false)
  const [editingRow, setEditingRow] = useState<AnnualKPIRow | null>(null)
  const [deletingRow, setDeletingRow] = useState<AnnualKPIRow | null>(null)
  const [realisasiAsgn, setRealisasiAsgn] = useState<{
    asgn: KPIAssignment
    q: 'Q1' | 'Q2' | 'Q3' | 'Q4'
  } | null>(null)

  // ── Periods ───────────────────────────────────────────────────────────────

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const periods = periodsData?.data ?? []

  const availableYears = useMemo(() => {
    const ys = new Set(periods.map((p) => p.year))
    ys.add(currentYear)
    return Array.from(ys).sort((a, b) => b - a)
  }, [periods, currentYear])

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears])

  const yearPeriods: YearPeriods = useMemo(
    () => ({
      Q1: periods.find((p) => p.year === selectedYear && p.quarter === 1),
      Q2: periods.find((p) => p.year === selectedYear && p.quarter === 2),
      Q3: periods.find((p) => p.year === selectedYear && p.quarter === 3),
      Q4: periods.find((p) => p.year === selectedYear && p.quarter === 4),
    }),
    [periods, selectedYear]
  )

  // ── KPI Plan ──────────────────────────────────────────────────────────────

  const { data: plan, refetch: refetchPlan } = useQuery<KPIPlan>({
    queryKey: ['my-kpi-plan', selectedYear, user?.id],
    queryFn: () => penilaianApi.getMyKPIPlan(selectedYear),
    enabled: !!user?.id,
  })

  const submitMutation = useMutation({
    mutationFn: () => penilaianApi.submitMyKPIPlan(selectedYear),
    onSuccess: () => {
      refetchPlan()
      toast.success('Rencana KPI berhasil dikirim untuk persetujuan')
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal mengirim rencana KPI'),
  })

  // ── KPI Assignments ───────────────────────────────────────────────────────

  const empId = user?.id ?? ''

  const q1Query = useQuery({
    queryKey: ['my-kpi-annual', yearPeriods.Q1?.id, empId],
    queryFn: () =>
      penilaianApi.getEmployeeKPIAssignments(yearPeriods.Q1!.id, empId),
    enabled: !!yearPeriods.Q1 && !!empId,
  })
  const q2Query = useQuery({
    queryKey: ['my-kpi-annual', yearPeriods.Q2?.id, empId],
    queryFn: () =>
      penilaianApi.getEmployeeKPIAssignments(yearPeriods.Q2!.id, empId),
    enabled: !!yearPeriods.Q2 && !!empId,
  })
  const q3Query = useQuery({
    queryKey: ['my-kpi-annual', yearPeriods.Q3?.id, empId],
    queryFn: () =>
      penilaianApi.getEmployeeKPIAssignments(yearPeriods.Q3!.id, empId),
    enabled: !!yearPeriods.Q3 && !!empId,
  })
  const q4Query = useQuery({
    queryKey: ['my-kpi-annual', yearPeriods.Q4?.id, empId],
    queryFn: () =>
      penilaianApi.getEmployeeKPIAssignments(yearPeriods.Q4!.id, empId),
    enabled: !!yearPeriods.Q4 && !!empId,
  })

  const isLoading =
    q1Query.isLoading || q2Query.isLoading || q3Query.isLoading || q4Query.isLoading

  // ── Merge by KPI name ─────────────────────────────────────────────────────

  const mergedRows = useMemo((): AnnualKPIRow[] => {
    const map = new Map<string, AnnualKPIRow>()
    const add = (asgn: KPIAssignment, qKey: 'q1' | 'q2' | 'q3' | 'q4') => {
      const name = asgn.kpi?.name ?? '(Tanpa Nama)'
      if (!map.has(name)) {
        map.set(name, {
          name,
          description: asgn.kpi?.target_description ?? null,
          annual_target: asgn.kpi?.annual_target ?? null,
          kpi_type: (asgn.kpi?.kpi_type as 'company' | 'individual') ?? 'individual',
          department: asgn.kpi?.department,
        })
      }
      map.get(name)![qKey] = asgn
    }
    ;(q1Query.data ?? []).forEach((a) => add(a, 'q1'))
    ;(q2Query.data ?? []).forEach((a) => add(a, 'q2'))
    ;(q3Query.data ?? []).forEach((a) => add(a, 'q3'))
    ;(q4Query.data ?? []).forEach((a) => add(a, 'q4'))
    return Array.from(map.values())
  }, [q1Query.data, q2Query.data, q3Query.data, q4Query.data])

  const companyRows = mergedRows.filter((r) => r.kpi_type === 'company')
  const individualRows = mergedRows.filter((r) => r.kpi_type === 'individual')

  const isApproved = plan?.status === 'approved'
  const canEdit = !plan || ['draft', 'rejected_by_manager', 'rejected_by_hr'].includes(plan.status)
  const hasPeriods = !!(
    yearPeriods.Q1 || yearPeriods.Q2 || yearPeriods.Q3 || yearPeriods.Q4
  )

  const totalBobot = mergedRows.reduce(
    (s, r) => s + (r.q1?.bobot ?? r.q2?.bobot ?? r.q3?.bobot ?? r.q4?.bobot ?? 0),
    0
  )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Saya</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tambahkan KPI individu Anda — target berbeda per kuartal
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Tahun</span>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Quarter chips */}
      <div className="flex items-center gap-2 mb-4">
        {(['Q1', 'Q2', 'Q3', 'Q4'] as const).map((q) => {
          const p = yearPeriods[q]
          return (
            <span
              key={q}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                p ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${p ? 'bg-blue-500' : 'bg-gray-300'}`}
              />
              {q}
              {p ? ` · ${p.status}` : ' · —'}
            </span>
          )
        })}
      </div>

      {/* Plan status bar */}
      {plan && (
        <div
          className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${
            isApproved
              ? 'bg-green-50 border-green-200'
              : plan.status === 'rejected_by_manager' || plan.status === 'rejected_by_hr'
              ? 'bg-red-50 border-red-200'
              : plan.status === 'pending_manager'
              ? 'bg-yellow-50 border-yellow-200'
              : plan.status === 'pending_hr'
              ? 'bg-blue-50 border-blue-200'
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <PlanStatusBadge status={plan.status} />
            {plan.manager_comment && (
              <span className="text-sm text-gray-600">
                Catatan:{' '}
                <span className="font-medium">{plan.manager_comment}</span>
              </span>
            )}
            {isApproved && (
              <span className="text-sm text-green-700 font-medium">
                Rencana KPI terkunci — tidak bisa diubah lagi
              </span>
            )}
          </div>
          {['draft', 'rejected_by_manager', 'rejected_by_hr'].includes(plan.status) && hasPeriods && (
            <button
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending || totalBobot === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {submitMutation.isPending ? 'Mengirim…' : 'Kirim untuk Persetujuan'}
            </button>
          )}
          {plan.status === 'pending_manager' && (
            <span className="text-sm text-yellow-700">Menunggu persetujuan atasan</span>
          )}
          {plan.status === 'pending_hr' && (
            <span className="text-sm text-blue-700">Menunggu persetujuan HR</span>
          )}
        </div>
      )}

      {!hasPeriods ? (
        <div className="text-center py-20 text-gray-400 bg-white border border-dashed border-gray-300 rounded-xl">
          <p className="font-medium">
            Belum ada periode untuk tahun {selectedYear}
          </p>
          <p className="text-sm mt-1">
            Hubungi HR untuk membuat periode Q1–Q4
          </p>
        </div>
      ) : isLoading ? (
        <div className="text-center py-12 text-gray-500">Memuat KPI…</div>
      ) : (
        <div className="space-y-4">
          {/* Summary */}
          <div className="flex gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div>
              <div className="text-xs text-blue-600 font-medium">Total KPI</div>
              <div className="text-xl font-bold text-blue-700">
                {mergedRows.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-blue-600 font-medium">Total Bobot</div>
              <div
                className={`text-xl font-bold ${
                  totalBobot === 100 ? 'text-green-600' : 'text-amber-600'
                }`}
              >
                {totalBobot}%
              </div>
            </div>
          </div>

          {/* Bobot warning */}
          {totalBobot !== 100 && individualRows.length > 0 && (
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Total bobot harus 100% — saat ini {totalBobot}%
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {totalBobot < 100
                    ? `Kurang ${100 - totalBobot}% — sesuaikan bobot KPI Individu Anda hingga totalnya tepat 100%.`
                    : `Lebih ${totalBobot - 100}% — kurangi bobot KPI Individu Anda hingga totalnya tepat 100%.`}
                </p>
              </div>
            </div>
          )}

          {/* Corporate KPIs (read-only) */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">KPI Perusahaan</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Ditetapkan oleh HR — tidak dapat diubah
              </p>
            </div>
            {companyRows.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                Belum ada KPI Perusahaan untuk tahun ini
              </div>
            ) : (
              <KPIAnnualTable
                rows={companyRows}
                yearPeriods={yearPeriods}
                canEdit={false}
              />
            )}
          </div>

          {/* Individual KPIs */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">KPI Individu</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {isApproved
                    ? 'Terkunci setelah disetujui'
                    : !canEdit
                    ? 'Terkunci selama menunggu persetujuan'
                    : 'Tambahkan dan kelola KPI Anda sendiri'}
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => setAddModal(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                >
                  + Tambah KPI
                </button>
              )}
            </div>
            {individualRows.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                {canEdit
                  ? 'Belum ada KPI Individu — klik "+ Tambah KPI" untuk memulai'
                  : 'Tidak ada KPI Individu'}
              </div>
            ) : (
              <KPIAnnualTable
                rows={individualRows}
                yearPeriods={yearPeriods}
                canEdit={canEdit}
                onEdit={setEditingRow}
                onDelete={setDeletingRow}
                onEditRealisasi={(asgn, q) => setRealisasiAsgn({ asgn, q })}
              />
            )}
          </div>
        </div>
      )}

      {/* Add modal */}
      {addModal && (
        <EditKPIModal
          row={null}
          yearPeriods={yearPeriods}
          employeeId={empId}
          year={selectedYear}
          onClose={() => setAddModal(false)}
        />
      )}

      {/* Edit modal */}
      {editingRow && (
        <EditKPIModal
          row={editingRow}
          yearPeriods={yearPeriods}
          employeeId={empId}
          year={selectedYear}
          onClose={() => setEditingRow(null)}
        />
      )}

      {/* Delete modal */}
      {deletingRow && (
        <DeleteKPIModal
          row={deletingRow}
          yearPeriods={yearPeriods}
          employeeId={empId}
          onClose={() => setDeletingRow(null)}
        />
      )}

      {/* Realisasi modal */}
      {realisasiAsgn && (
        <RealisasiModal
          asgn={realisasiAsgn.asgn}
          quarterLabel={realisasiAsgn.q}
          periodId={yearPeriods[realisasiAsgn.q]?.id ?? ''}
          employeeId={empId}
          onClose={() => setRealisasiAsgn(null)}
        />
      )}
    </div>
  )
}
